import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

/**
 * Custom hook to determine template management context (Platform/Club/Branch)
 *
 * Replaces the duplicated context detection logic across all template pages.
 *
 * @returns {Object} Context information
 * @returns {boolean} isPlatformAdmin - True if in platform admin context
 * @returns {boolean} isClubAdmin - True if in club admin context
 * @returns {boolean} isBranchAdmin - True if in branch admin context
 * @returns {boolean} isCoach - True if in coach context
 * @returns {string|null} branchId - Branch ID if in branch context
 * @returns {string} contextType - One of: 'platform', 'club', 'branch'
 */
export const useTemplateContext = () => {
  const location = useLocation();
  const currentPath = location.pathname;
  const { user } = useAuth();

  return useMemo(() => {
    const isPlatformAdmin = currentPath.startsWith('/admin/system-templates');
    const isClubAdmin = currentPath.startsWith('/admin/templates');
    const branchMatch = currentPath.match(/^\/branch\/([^/]+)/);
    const isBranchAdmin = !!branchMatch;
    
    // Coaches use branch-level templates (their assigned location/branch)
    const isCoach = currentPath.startsWith('/coach/templates') || currentPath.startsWith('/coach/curriculum');
    
    // Get branch ID from URL (branch admin) or user's location (coach)
    let branchId = branchMatch?.[1] || null;
    if (isCoach && !branchId && user) {
      branchId = user.location_id || user.branch_id || null;
      console.log('[useTemplateContext] Coach branchId extraction:', {
        user_location_id: user.location_id,
        user_branch_id: user.branch_id,
        resolved_branchId: branchId,
        user_roles: user.roles
      });
    }

    // Determine context type for state pattern
    let contextType = 'platform';
    if (isBranchAdmin || isCoach) {
      // Coaches use branch-level template management (same as branch admins)
      contextType = 'branch';
    } else if (isClubAdmin) {
      contextType = 'club';
    }

    return {
      isPlatformAdmin,
      isClubAdmin,
      isBranchAdmin,
      isCoach,
      branchId,
      contextType,
      currentPath,
      userId: user?.id || null
    };
  }, [currentPath, user]);
};
